// PlayerCompareModal — side-by-side comparison of two players (market
// candidates or roster): per-stat rows with the better value highlighted,
// plus the contract numbers that matter for the decision.

import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { backdropVariants, modalVariants, useMotionVariants } from '../motion/motionTokens'
import { ModalCloseButton } from './ModalCloseButton'
import { PlayerAvatar } from './PlayerAvatar'
import { playerPortraitSrc } from '../helpers/portraits'
import {
  STAT_KEYS, STAT_LABELS, formatPotential, type Player,
} from '@/config/playersConfig'

function CompareRow({ label, a, b, higherWins = true, format }: {
  label: string
  a: number
  b: number
  higherWins?: boolean
  format?: (v: number) => string
}) {
  const aWins = higherWins ? a > b : a < b
  const bWins = higherWins ? b > a : b < a
  const fmt = format ?? ((v: number) => `${v}`)
  const winStyle = { color: 'var(--padel-green)', fontWeight: 800 as const }
  const loseStyle = { color: 'var(--text-secondary)', fontWeight: 600 as const }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem' }}>
      <span style={{ flex: 1, textAlign: 'right', ...(aWins ? winStyle : loseStyle) }}>{fmt(a)}</span>
      <span style={{ width: 120, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.68rem', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ flex: 1, ...(bWins ? winStyle : loseStyle) }}>{fmt(b)}</span>
    </div>
  )
}

export interface PlayerCompareModalProps {
  players: [Player, Player] | null
  scouted: boolean
  onClose: () => void
}

export function PlayerCompareModal({ players, scouted, onClose }: PlayerCompareModalProps) {
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)

  return createPortal(
    <AnimatePresence>
      {players && (
        <>
          <motion.div
            variants={backdrop} initial="initial" animate="enter" exit="exit"
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 210 }}
          />
          <motion.div
            variants={modal} initial="initial" animate="enter" exit="exit"
            role="dialog" aria-modal="true"
            style={{
              position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%',
              zIndex: 211,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              width: 'min(480px, 94vw)',
              maxHeight: '90vh', overflowY: 'auto',
              padding: '1.3rem 1.4rem',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
            }}
          >
            <ModalCloseButton onClick={onClose} />

            {/* Heads */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              {[players[0], players[1]].map((p, i) => (
                <div key={p.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', order: i === 0 ? 0 : 2 }}>
                  <PlayerAvatar id={p.id} name={p.name} size={48} src={playerPortraitSrc(p)} />
                  <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-strong)', textAlign: 'center' }}>
                    {p.name}
                  </div>
                  <div className="meta-text">{p.age} años</div>
                </div>
              ))}
              <div style={{ order: 1, fontFamily: 'var(--font-titles)', fontWeight: 800, color: 'var(--gold)' }}>
                VS
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <CompareRow label="Nivel" a={players[0].level} b={players[1].level} />
              {STAT_KEYS.map(key => (
                <CompareRow
                  key={key}
                  label={STAT_LABELS[key]}
                  a={players[0].stats?.[key] ?? players[0].level}
                  b={players[1].stats?.[key] ?? players[1].level}
                />
              ))}
              <div style={{ borderTop: '1px solid var(--glass-border)', margin: '0.3rem 0' }} />
              <CompareRow label="Forma" a={players[0].form} b={players[1].form} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem' }}>
                <span style={{ flex: 1, textAlign: 'right', color: 'var(--gold)', fontWeight: 700 }}>
                  {formatPotential(players[0], scouted)}
                </span>
                <span style={{ width: 120, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.68rem', flexShrink: 0 }}>
                  Potencial
                </span>
                <span style={{ flex: 1, color: 'var(--gold)', fontWeight: 700 }}>
                  {formatPotential(players[1], scouted)}
                </span>
              </div>
              <CompareRow
                label="Fichaje" higherWins={false}
                a={players[0].signingFee} b={players[1].signingFee}
                format={v => `${v.toLocaleString('es-ES')}€`}
              />
              <CompareRow
                label="Salario/sem" higherWins={false}
                a={players[0].salary} b={players[1].salary}
                format={v => `${v.toLocaleString('es-ES')}€`}
              />
            </div>

            {!scouted && (
              <div className="hint-box" style={{ marginTop: '0.9rem', fontSize: '0.7rem' }}>
                El potencial exacto está oculto: un entrenador con táctica 60+ te daría el dato real.
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
